import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useNotificationsStore } from '@/store/notificationsStore';
import { useValidationRulesStore } from '@/store/validationRulesStore';
import { useTableDefinitionsStore } from '@/store/tableDefinitionsStore';
import type { TableEntry } from '@/api/endpoints/tableDefinitions';
import { useFooterStore } from '@/stores/footerStore';
import { useDataViewAuditFilterStore } from '@/stores/dataViewAuditFilterStore';
import { useUserPrefFormatting } from '@/composables/useUserPrefFormatting';
import { extractErrorMessage, getProblemFromAxiosError } from '@/helpers/errorHandlingHelpers';
import { buildQueryColumnsFromDataViewRun, findDataViewContractViolations, tableHasAddableNewRowColumns } from '@/helpers/dataViewColumnDisplay';
import { buildRunDataViewRequest } from '@/helpers/dataViewRunRequest';
import { buildFreeTextFromTerm } from '@/helpers/dataViewFreeTextSearch';
import {
  getColumnDisplayMetadata,
  getDataViewById,
  resolveDataViewIdByPublicId,
  runDataView,
  runDataViewRow,
  updateDataViewColumnDisplayPreferences,
  updateMyDataViewColumnDisplayPreferences
} from '@/api/endpoints/dataViews';
import { getDataModelById } from '@/api/endpoints/dataModels';
import type { QueryColumn, QueryFilterItem, QueryOrderByItem, QueryResult } from '@/api/contracts/queryResult';
import type {
  DataViewColumn,
  DataViewColumnDisplayItem,
  DataViewColumnDisplayPreferenceEntry,
  DataViewResponse,
  UpdateDataViewColumnDisplayPreferencesRequest
} from '@/api/contracts/dataModel';

export interface DataViewColumnDisplayCache {
  items: DataViewColumnDisplayItem[];
  preferences: DataViewColumnDisplayPreferenceEntry[];
}

export interface AvailableViewItem {
  viewId: string;
  publicId: string;
  name: string;
  isDefault?: boolean;
  routeName?: 'data-view-public' | 'table-public' | 'report-public';
}

export function getRowId (row: Record<string, unknown>): number | null {
  const id = row['icsRowId_'] ?? row['ICSRowId_'];
  if (id == null) return null;
  const n = Number(id);
  return Number.isNaN(n) ? null : n;
}

const ATTACHMENT_INDICATOR_FIELD = '_attachment_indicator';

// WI 2200: relasjonskolonner som backend har flagget som ugyldige (verdi utenfor koblet liste).
// columnId (= QueryColumn.field) leveres i radens metadata; markeres på lik linje med value-list-feil.
function relationInvalidFieldsForRow (row: Record<string, unknown>): string[] {
  const metadata = row._metadata as { invalidRelationColumns?: string[] | null } | undefined;
  const invalid = metadata?.invalidRelationColumns;
  return Array.isArray(invalid) ? invalid : [];
}

function buildCellErrorMapForRow (
  row: Record<string, unknown>,
  columns: QueryColumn[]
): Map<string, true> | null {
  const validationStore = useValidationRulesStore();
  let cellMap: Map<string, true> | null = null;
  for (const column of columns) {
    if (column.field === ATTACHMENT_INDICATOR_FIELD) continue;
    const ruleColumnName =
      column.sourceColumnName?.trim()
      || column.columnName?.trim()
      || column.columnKey?.trim()
      || column.field;
    const value = row[column.field];
    if (validationStore.isValidValueForColumn(ruleColumnName, value) === false) {
      cellMap ??= new Map<string, true>();
      cellMap.set(column.field, true);
    }
  }
  for (const invalidField of relationInvalidFieldsForRow(row)) {
    cellMap ??= new Map<string, true>();
    cellMap.set(invalidField, true);
  }
  return cellMap;
}

function buildValidationByRowId (
  rows: Record<string, unknown>[],
  columns: QueryColumn[]
): Map<number, Map<string, true>> {
  const out = new Map<number, Map<string, true>>();
  for (const row of rows) {
    const rowId = getRowId(row);
    if (rowId == null) continue;
    const cellMap = buildCellErrorMapForRow(row, columns);
    if (cellMap) out.set(rowId, cellMap);
  }
  return out;
}

function isGuidLike (value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

interface DataViewErrorSignals {
  hasLegacySignal: boolean;
  hasMaterializationDriftSignal: boolean;
  hasOrphanSignal: boolean;
  hasAccessSignal: boolean;
}

// Classify the backend detail string into the known runDataView error signals.
// hasAccessSignal folds the string-typed access check (detail contains tilgang)
// so the caller only needs to add the status-403 disjunction.
function detectErrorSignals (detail: unknown): DataViewErrorSignals {
  const isString = typeof detail === 'string';
  const d = isString ? detail.toLowerCase() : '';
  return {
    hasLegacySignal: isString && (detail.includes('oppdateres') || detail.includes('tableKey')),
    hasMaterializationDriftSignal: isString && (
      detail.includes('Regenerer materialisering')
      || detail.includes('materialiserte view-objektet for datamodellen er utdatert')
    ),
    hasOrphanSignal: d.includes('orphan')
      || d.includes('utdaterte koblinger')
      || d.includes('ikke lenger finnes i datamodellen')
      || d.includes('ugyldig queryplan'),
    hasAccessSignal: d.includes('tilgang')
  };
}

// Oversetter kjente backend-feilsignaler fra runDataView til brukerrettede meldinger.
function dataViewRunErrorMessage (err: unknown): string {
  const ax = err as { response?: { status?: number; data?: { title?: string; detail?: string; errors?: unknown } } };
  const status = ax?.response?.status;
  const detail = ax?.response?.data?.detail ?? '';
  const title = ax?.response?.data?.title ?? '';
  const errorsField = ax?.response?.data?.errors;
  const errors: string[] = Array.isArray(errorsField)
    ? errorsField.filter((e): e is string => typeof e === 'string')
    : [];

  const { hasLegacySignal, hasMaterializationDriftSignal, hasOrphanSignal, hasAccessSignal } = detectErrorSignals(detail);

  if (hasLegacySignal) {
    return 'Datamodellen er i gammelt format og må oppdateres. Be en administrator eller eier av modellen om å åpne modellen og bruke «Oppdater modell».';
  }
  if (hasMaterializationDriftSignal) {
    const lines = ['Datavisningen kan ikke lastes — det materialiserte view-objektet er utdatert.'];
    if (errors.length > 0) {
      lines.push('Detaljer:');
      errors.forEach((e) => lines.push(` • ${e}`));
    }
    lines.push('Åpne datamodellen og trykk «Regenerer materialisering» for å oppdatere view-en.');
    return lines.join('\n');
  }
  if (hasOrphanSignal) {
    if (errors.length > 0) {
      return [
        'Datavisningen inneholder utdaterte koblinger etter endringer i datamodellen.',
        'Detaljer:',
        ...errors.map((e) => ` • ${e}`)
      ].join('\n');
    }
    return 'Datavisningen inneholder utdaterte koblinger etter endringer i datamodellen. Noen data kan derfor ikke vises akkurat nå.';
  }
  if (status === 403 || hasAccessSignal) {
    return 'Du har ikke tilgang til å vise denne datavisningen. Be en administrator om å gi deg tilgang.';
  }
  if (status === 404) {
    return 'Datavisningen eller tilhørende modell ble ikke funnet. Den kan være slettet eller flyttet.';
  }
  if (errors.length > 0) {
    return [title || 'Forespørselen feilet', ...errors.map((e) => ` • ${e}`)].filter(Boolean).join('\n');
  }
  if (title || detail) {
    return [title, detail].filter(Boolean).join(': ');
  }
  return 'Kunne ikke laste datavisning. Prøv igjen senere eller kontakt support hvis feilen vedvarer.';
}

// Feilfangst for lagring av visningsalternativer eies her (ikke i komponenten):
// ved feil varsler storen brukeren (addProblem/addError) og signaliserer fiasko
// til kalleren via retur-verdien, slik at UI-laget bare styrer dialog-tilstand.
function notifySavePreferencesFailure (e: unknown): void {
  const notifications = useNotificationsStore();
  const problem = getProblemFromAxiosError(e);
  if (problem) {
    notifications.addProblem(problem);
  } else {
    notifications.addError('Feil', 'Kunne ikke lagre visningsalternativer');
  }
  console.error('[DataView preferences] kunne ikke lagre visningsalternativer', e);
}

async function saveMyColumnDisplayPreferences (
  viewId: string,
  request: UpdateDataViewColumnDisplayPreferencesRequest,
  signal?: AbortSignal
): Promise<boolean> {
  try {
    await updateMyDataViewColumnDisplayPreferences(viewId, request, signal);
    return true;
  } catch (e: unknown) {
    notifySavePreferencesFailure(e);
    return false;
  }
}

export const useQueryStore = defineStore('dataViewQuery', () => {
  const columns = ref<QueryColumn[]>([]);
  const rows = ref<Record<string, unknown>[]>([]);
  const total = ref(0);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const selectedRows = ref<Record<string, unknown>[]>([]);
  const validationByRowId = ref<Map<number, Map<string, true>>>(new Map());

  const selectedRowIds = computed<Set<number>>(() => {
    const s = new Set<number>();
    for (const row of selectedRows.value) {
      const id = getRowId(row);
      if (id != null) s.add(id);
    }
    return s;
  });

  function hasValidationErrorForCell (rowId: number | null, columnField: string): boolean {
    if (rowId == null) return false;
    return validationByRowId.value.get(rowId)?.get(columnField) === true;
  }

  const filters = ref<QueryFilterItem[]>([]);
  const orderBy = ref<QueryOrderByItem[]>([]);
  const freeTextTerm = ref<string>('');

  const currentView = ref<DataViewResponse | null>(null);
  const availableViews = ref<AvailableViewItem[]>([]);
  const mainTableKey = ref<string | null>(null);
  /**
   * Bug 2122: sant når hovedtabellen har minst én redigerbar ny-rad-kolonne. Settes pålitelig fra
   * den awaited tabell-definisjons-hentingen (ikke via publicId-cache-oppslag), slik at
   * "Legg til rad"-knappen virker i både datavisning og tabell-modus, uavhengig av om
   * `mainTableKey` er identisk med tabellens publicId.
   */
  const mainTableHasAddableColumns = ref(false);
  /** Datamodellens hovedtabell-alias (alias), for klassifisering av kolonner i datavisning. */
  const mainAlias = ref<string | null>(null);

  const columnDisplayByViewId = ref<Map<string, DataViewColumnDisplayCache>>(new Map());

  let _refreshCallback: (() => Promise<void>) | null = null;

  function getColumnDisplayForView (viewId: string): DataViewColumnDisplayCache | undefined {
    return columnDisplayByViewId.value.get(viewId);
  }

  function setColumnDisplayForView (viewId: string, cache: DataViewColumnDisplayCache): void {
    const next = new Map(columnDisplayByViewId.value);
    next.set(viewId, cache);
    columnDisplayByViewId.value = next;
  }

  function clearColumnDisplayForView (viewId: string): void {
    if (!columnDisplayByViewId.value.has(viewId)) return;
    const next = new Map(columnDisplayByViewId.value);
    next.delete(viewId);
    columnDisplayByViewId.value = next;
  }

  // ── data loading ────────────────────────────────────────────────────────────

  function load (result: QueryResult): void {
    columns.value = result.columns;
    rows.value = result.rows;
    total.value = result.total;
    selectedRows.value = [];
    validationByRowId.value = buildValidationByRowId(result.rows, result.columns);
    filters.value.forEach((f) => {
      const col = columns.value.find((c) => c.field === f.columnName);
      if (col) { col.filterOperator = f.operator; col.filterValue = f.value; }
    });
    orderBy.value.forEach((o) => {
      const col = columns.value.find((c) => c.field === o.columnName);
      if (col) col.sortOrder = o.direction;
    });
    notifyDataViewContractViolations(result.columns);
  }

  // WI #1279: surface backend-kontraktsbrudd som eksplisitt feilmelding.
  // Hvis en kolonne er flagget skrivbar (isReadOnly=false) uten å levere
  // columnKey, ville save tidligere stille feile med 400 etter at FE-en
  // fallback'et til SQL-aliasen. Nå viser vi en konkret melding.
  function notifyDataViewContractViolations (cols: QueryColumn[]): void {
    const violations = findDataViewContractViolations(cols);
    if (violations.length === 0) return;
    const notifications = useNotificationsStore();
    for (const v of violations) {
      notifications.addError(
        'Datavisning: ugyldig kontrakt',
        `Kolonne "${v.label}" er flagget som skrivbar uten kontrakts-nøkkel fra backend. Endringer kan ikke lagres. Kontakt support.`
      );
      console.error('[DataView contract] writable column without columnKey', v);
    }
  }

  function setLoading (value: boolean): void {
    loading.value = value;
  }

  function setError (message: string): void {
    error.value = message;
  }

  function clearError (): void {
    error.value = null;
  }

  // ── refresh callback ─────────────────────────────────────────────────────────

  function setRefreshCallback (cb: () => Promise<void>): void {
    _refreshCallback = cb;
  }

  async function refreshQuery (): Promise<void> {
    if (!_refreshCallback) return;
    error.value = null;
    try {
      await _refreshCallback();
    } catch (e: unknown) {
      const notifications = useNotificationsStore();
      const problem = getProblemFromAxiosError(e);
      const msg = extractErrorMessage(e);
      error.value = msg;
      if (problem) {
        notifications.addProblem(problem);
      } else {
        notifications.addError('Kunne ikke laste datavisning', msg);
      }
    }
  }

  function reselectRowById (rowId: number): void {
    const row = rows.value.find((r) => getRowId(r) === rowId);
    selectedRows.value = row != null ? [row] : [];
  }

  async function fetchRowAndReplace (viewId: string, rowId: number): Promise<void> {
    try {
      const result = await runDataViewRow(viewId, rowId);
      if (!result.columns?.length || !result.items?.length) {
        reselectRowById(rowId);
        return;
      }
      const freshRecord: Record<string, unknown> = {};
      result.columns.forEach((col, idx) => {
        freshRecord[col.columnId] = (result.items[0] as unknown[])[idx];
      });
      const metaByRowId = result.rowMetadataByRowId;
      if (metaByRowId?.[String(rowId)]) {
        freshRecord._metadata = metaByRowId[String(rowId)];
      }
      const idx = rows.value.findIndex((r) => getRowId(r) === rowId);
      if (idx >= 0) {
        rows.value.splice(idx, 1, freshRecord);
        const cellMap = buildCellErrorMapForRow(freshRecord, columns.value);
        const next = new Map(validationByRowId.value);
        if (cellMap) next.set(rowId, cellMap);
        else next.delete(rowId);
        validationByRowId.value = next;
      }
      reselectRowById(rowId);
    } catch (e: unknown) {
      const notifications = useNotificationsStore();
      const problem = getProblemFromAxiosError(e);
      const msg = extractErrorMessage(e);
      if (problem) {
        notifications.addProblem(problem);
      } else {
        notifications.addError('Kunne ikke hente rad på nytt', msg);
      }
      reselectRowById(rowId);
    }
  }

  // ── view-/query-lasting (flyttet fra DataViewDataView; feilfangst eies her) ──

  /**
   * Sett hovedtabell-nøkkel og hent tabelldefinisjonen. Avleder `mainTableHasAddableColumns` fra
   * retur-verdien (ikke fra et publicId-cache-oppslag), slik at "Legg til rad"-knappen aktiveres
   * pålitelig så snart hentingen er ferdig. Kallere venter ikke på dette (knappen oppdateres reaktivt).
   */
  function setMainTableKeyAndPrime (key: string | null): void {
    setMainTableKey(key);
    mainTableHasAddableColumns.value = false;
    const trimmed = String(key ?? '').trim();
    if (!trimmed) return;
    const tableDefinitionsStore = useTableDefinitionsStore();
    void tableDefinitionsStore.fetchTable(trimmed, { silent: true }).then((table: TableEntry | undefined) => {
      // Ignorer utdaterte svar dersom hovedtabellen er byttet i mellomtiden.
      if (mainTableKey.value === trimmed) {
        mainTableHasAddableColumns.value = tableHasAddableNewRowColumns(table);
      }
    });
  }

  /**
   * Slå opp viewId (GUID direkte eller via publicId) og hent datavisningen.
   * Feil fanges her og gir { viewId: '', view: null } — hosten viser «ikke funnet».
   */
  async function resolveAndFetchView (routeId: string): Promise<{ viewId: string; view: DataViewResponse | null }> {
    try {
      const resolved = isGuidLike(routeId) ? routeId : await resolveDataViewIdByPublicId(routeId);
      if (!resolved) {
        return { viewId: '', view: null };
      }
      const view = await getDataViewById(resolved);
      return { viewId: resolved, view };
    } catch {
      return { viewId: '', view: null };
    }
  }

  /**
   * Hent datamodellen for gjeldende view og sett mainAlias + hovedtabell-nøkkel.
   * Feil fanges her og nullstiller kontekst (uendret fallback-adferd fra hosten).
   */
  async function loadModelContext (modelId: string): Promise<void> {
    try {
      const m = await getDataModelById(modelId);
      setMainAlias(m.mainAlias ?? null);
      const alias = String(m.mainAlias || '').trim();
      const mainTable = (m.tables || []).find((table) => String(table.alias || '').trim() === alias);
      const resolvedKey = String(mainTable?.tableKey || '').trim() || null;
      setMainTableKeyAndPrime(resolvedKey);
    } catch {
      setMainAlias(null);
      setMainTableKeyAndPrime(null);
    }
  }

  /**
   * Kjør datavisnings-/tabell-spørringen med gjeldende filtre/sortering/fritekst.
   * Feil fanges her og settes som reaktiv feiltilstand (error) for hosten.
   */
  async function runQuery (options: { tableMode?: boolean; tableKey?: string | null } = {}): Promise<void> {
    const footerStore = useFooterStore();
    const auditStore = useDataViewAuditFilterStore();
    const validationRulesStore = useValidationRulesStore();
    // WI 2086: radtall i footeren følger brukerens tallformat-prefs.
    const { formatNumber } = useUserPrefFormatting();

    if (options.tableMode === true) {
      const tableKey = String(options.tableKey ?? '').trim();
      if (!tableKey) return;
      await validationRulesStore.setTableKey(tableKey);

      const request = buildRunDataViewRequest({
        filters: filters.value,
        orderBy: orderBy.value,
        freeText: buildFreeTextFromTerm(freeTextTerm.value),
        changes: auditStore.currentFilter
      });

      setLoading(true);
      try {
        const result = await runDataView(tableKey, request);
        const queryColumns = buildQueryColumnsFromDataViewRun(result.columns, [], tableKey, null);
        const mappedRows: Record<string, unknown>[] = result.items.map((itemRow: unknown[]) => {
          const record: Record<string, unknown> = {};
          result.columns.forEach((col: DataViewColumn, idx: number) => {
            record[col.columnId] = itemRow[idx];
          });
          return record;
        });
        load({
          columns: queryColumns,
          rows: mappedRows,
          total: result.total,
          page: result.page,
          pageSize: result.pageSize
        });
        setMainTableKeyAndPrime(String(result.tableKey || tableKey));
        footerStore.set(`Antall rader: ${formatNumber(result.total)}`);
      } catch (err: unknown) {
        setError(dataViewRunErrorMessage(err));
      } finally {
        setLoading(false);
      }
      return;
    }

    const id = currentView.value?.viewId;
    if (!id) return;

    const request = buildRunDataViewRequest({
      filters: filters.value,
      orderBy: orderBy.value,
      freeText: buildFreeTextFromTerm(freeTextTerm.value),
      changes: auditStore.currentFilter
    });

    setLoading(true);
    try {
      const displayBundlePromise = (async () => {
        const cached = getColumnDisplayForView(id);
        if (cached) return cached;
        const d = await getColumnDisplayMetadata(id);
        const bundle = {
          items: d.columns,
          preferences: d.columnDisplayPreferences ?? []
        };
        setColumnDisplayForView(id, bundle);
        return bundle;
      })();

      const [displayBundle, result] = await Promise.all([
        displayBundlePromise,
        runDataView(id, request)
      ]);

      // US 2884: kjoere-responsen baerer allerede brukerens kolonneoppsett. Display-metadataen
      // hentes fortsatt fordi den beskriver felttype/komponentvalg/relasjonsoppslag, ikke oppsett.
      const queryColumns = buildQueryColumnsFromDataViewRun(
        result.columns,
        displayBundle.items,
        mainTableKey.value,
        mainAlias.value
      );

      const rowIdColumnIndex = result.columns.findIndex(
        (c: DataViewColumn) => c.columnId === 'ICSRowId_' || c.columnId === 'icsRowId_'
      );
      const metaByRowId = result.rowMetadataByRowId ?? null;

      const mappedRows: Record<string, unknown>[] = result.items.map((row: unknown[]) => {
        const record: Record<string, unknown> = {};
        result.columns.forEach((col: DataViewColumn, idx: number) => {
          record[col.columnId] = row[idx];
        });
        if (rowIdColumnIndex >= 0 && metaByRowId) {
          const raw = (row as unknown[])[rowIdColumnIndex];
          const rowId = raw != null ? Number(raw) : Number.NaN;
          if (!Number.isNaN(rowId)) {
            record._metadata = metaByRowId[String(rowId)] ?? {};
          }
        }
        return record;
      });

      const queryResult: QueryResult = {
        columns: queryColumns,
        rows: mappedRows,
        total: result.total,
        page: result.page,
        pageSize: result.pageSize,
        nextPageToken: result.nextPageToken
      };

      const tableKey = String(result.tableKey || '').trim() || null;
      setMainTableKeyAndPrime(tableKey);
      // Reglene må være lastet FØR load — valideringskartet bygges
      // imperativt der, med reglene som finnes i øyeblikket (bug 2077).
      await validationRulesStore.setTableKey(tableKey);
      load(queryResult);
      footerStore.set(`Antall rader: ${formatNumber(result.total)}`);
    } catch (err: unknown) {
      setError(dataViewRunErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  // ── selection ────────────────────────────────────────────────────────────────

  const hasSelectedRows = computed(() => selectedRows.value.length > 0);
  const selectedCount = computed(() => selectedRows.value.length);

  const getSelectionIcon = computed(() => {
    if (selectedRows.value.length === 0) return ['fal', 'square'];
    if (selectedRows.value.length === rows.value.length) return ['fas', 'square-check'];
    return ['fas', 'square-minus'];
  });

  function selectSingleRow (row: Record<string, unknown>): void {
    selectedRows.value = [row];
  }

  function toggleRowSelection (row: Record<string, unknown>): void {
    const idx = selectedRows.value.indexOf(row);
    if (idx >= 0) {
      selectedRows.value.splice(idx, 1);
    } else {
      selectedRows.value.push(row);
    }
  }

  function addRowSelection (row: Record<string, unknown>): void {
    if (!selectedRows.value.includes(row)) {
      selectedRows.value.push(row);
    }
  }

  function addRangeSelection (row: Record<string, unknown>): void {
    if (selectedRows.value.length === 0) {
      selectedRows.value = [row];
      return;
    }
    const lastSelected = selectedRows.value[selectedRows.value.length - 1];
    const lastIdx = rows.value.indexOf(lastSelected);
    const targetIdx = rows.value.indexOf(row);
    if (lastIdx < 0 || targetIdx < 0) return;
    const from = Math.min(lastIdx, targetIdx);
    const to = Math.max(lastIdx, targetIdx);
    for (let i = from; i <= to; i++) {
      const r = rows.value[i];
      if (r && !selectedRows.value.includes(r)) {
        selectedRows.value.push(r);
      }
    }
  }

  function clearSelection (): void {
    selectedRows.value = [];
  }

  function handleBulkSelection (mode: string): void {
    if (mode === 'all') {
      selectedRows.value = [...rows.value];
    } else if (mode === 'none' || mode === 'unselect') {
      selectedRows.value = [];
    } else if (mode === 'invert' || mode === 'inverted') {
      selectedRows.value = rows.value.filter((r) => !selectedRows.value.includes(r));
    }
  }

  // ── sort ─────────────────────────────────────────────────────────────────────

  function setSort (columnName: string, direction: 'ASC' | 'DESC'): void {
    const existing = orderBy.value.find((o) => o.columnName === columnName);
    if (existing) {
      existing.direction = direction;
    } else {
      orderBy.value.push({ columnName, direction });
    }
    const col = columns.value.find((c) => c.field === columnName);
    if (col) col.sortOrder = direction;
  }

  function clearSort (columnName?: string): void {
    if (columnName) {
      orderBy.value = orderBy.value.filter((o) => o.columnName !== columnName);
      const col = columns.value.find((c) => c.field === columnName);
      if (col) col.sortOrder = null;
    } else {
      orderBy.value = [];
      columns.value.forEach((c) => { c.sortOrder = null; });
    }
  }

  // ── filter ───────────────────────────────────────────────────────────────────

  function getFilter (columnName: string): QueryFilterItem[] {
    return filters.value.filter((f) => f.columnName === columnName);
  }

  function setFilter (item: QueryFilterItem): void {
    filters.value = filters.value.filter((f) => f.columnName !== item.columnName);
    filters.value.push(item);
    const col = columns.value.find((c) => c.field === item.columnName);
    if (col) {
      col.filterOperator = item.operator;
      col.filterValue = item.value;
    }
  }

  function clearFilter (columnName: string): void {
    filters.value = filters.value.filter((f) => f.columnName !== columnName);
    const col = columns.value.find((c) => c.field === columnName);
    if (col) {
      col.filterOperator = null;
      col.filterValue = undefined;
    }
  }

  function clearAllFilters (): void {
    filters.value = [];
    columns.value.forEach((c) => {
      c.filterOperator = null;
      c.filterValue = undefined;
    });
  }

  function setFreeText (term: string): void {
    freeTextTerm.value = term.trim();
  }

  function clearFreeText (): void {
    freeTextTerm.value = '';
  }

  function setCurrentView (view: DataViewResponse | null): void {
    currentView.value = view;
  }

  function setAvailableViews (views: AvailableViewItem[]): void {
    availableViews.value = views;
  }

  function setMainTableKey (id: string | null): void {
    const normalized = String(id ?? '').trim();
    mainTableKey.value = normalized.length > 0 ? normalized : null;
  }

  function setMainAlias (id: string | null): void {
    mainAlias.value = id;
  }

  function reset (): void {
    columns.value = [];
    rows.value = [];
    total.value = 0;
    loading.value = false;
    error.value = null;
    selectedRows.value = [];
    validationByRowId.value = new Map();
    filters.value = [];
    orderBy.value = [];
    freeTextTerm.value = '';
    _refreshCallback = null;
    currentView.value = null;
    availableViews.value = [];
    mainTableKey.value = null;
    mainTableHasAddableColumns.value = false;
    mainAlias.value = null;
  }

  // ── column resize ────────────────────────────────────────────────────────────

  function resizeColumn (field: string, width: number): void {
    const col = columns.value.find((c) => c.field === field);
    if (col) col.width = width;
  }

  async function saveColumnDisplayPreferences (
    viewId: string,
    request: UpdateDataViewColumnDisplayPreferencesRequest,
    signal?: AbortSignal
  ): Promise<DataViewResponse | null> {
    try {
      return await updateDataViewColumnDisplayPreferences(viewId, request, signal);
    } catch (e: unknown) {
      notifySavePreferencesFailure(e);
      return null;
    }
  }

  return {
    columns,
    rows,
    total,
    loading,
    error,
    selectedRows,
    selectedRowIds,
    filters,
    orderBy,
    freeTextTerm,
    currentView,
    availableViews,
    hasSelectedRows,
    selectedCount,
    getSelectionIcon,
    getRowId,
    hasValidationErrorForCell,
    load,
    setLoading,
    setError,
    clearError,
    setRefreshCallback,
    refreshQuery,
    reselectRowById,
    fetchRowAndReplace,
    setMainTableKeyAndPrime,
    resolveAndFetchView,
    loadModelContext,
    runQuery,
    selectSingleRow,
    toggleRowSelection,
    addRowSelection,
    addRangeSelection,
    clearSelection,
    handleBulkSelection,
    setSort,
    clearSort,
    getFilter,
    setFilter,
    clearFilter,
    clearAllFilters,
    setFreeText,
    clearFreeText,
    resizeColumn,
    setCurrentView,
    setAvailableViews,
    mainTableKey,
    mainTableHasAddableColumns,
    setMainTableKey,
    mainAlias,
    setMainAlias,
    getColumnDisplayForView,
    setColumnDisplayForView,
    clearColumnDisplayForView,
    saveColumnDisplayPreferences,
    saveMyColumnDisplayPreferences,
    reset,
  };
});
